import { useState } from "react";
import { Link, NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import { Bell, LogOut, Sprout, Menu, X, Search, Plus, LayoutGrid } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/lib/auth";
import { adminMenuFor, ROLE_LABELS, isAdminRole } from "@/lib/navigation";
import MobileTabBar from "@/components/MobileTabBar";

export default function AdminLayout() {
  const [open, setOpen] = useState(false);
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  // Tunggu sesi dimuat sebelum memutuskan akses.
  if (loading) return <div className="min-h-screen grid place-items-center text-ink/60">Memuat…</div>;
  // Guard: hanya role pengurus/admin yang boleh masuk dasbor manajemen.
  if (!user || !isAdminRole(user.role)) return <Navigate to="/masuk" replace />;

  // Menu sidebar disesuaikan dengan role yang login (RBAC).
  const nav = adminMenuFor(user.role);
  const initials = (user.name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("");

  const doLogout = () => { logout(); navigate("/masuk"); };

  // Navigasi bawah (mobile): 4 menu pertama sesuai role + "Menu" untuk sisanya.
  const tabs = [
    ...nav.slice(0, 4).map((n) => ({ to: n.to, end: n.end, label: n.short || n.label, icon: n.icon })),
    { label: "Menu", icon: LayoutGrid, onClick: () => setOpen(true), active: open },
  ];

  return (
    <div className="min-h-screen bg-[#FBFAF6]">
      {/* Latar gelap saat laci menu terbuka (mobile) */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-[45] bg-ink/50 backdrop-blur-[2px] animate-fade-in" onClick={() => setOpen(false)} />
      )}
      <aside className={`fixed inset-y-0 left-0 z-50 w-[82vw] max-w-64 lg:w-64 bg-ink text-cream/90 pt-safe pb-safe flex flex-col transform ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 transition-transform`}>
        <div className="px-5 pt-5 pb-3 flex items-center justify-between border-b border-white/10">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-lg bg-amber-warm text-ink grid place-items-center">
              <Sprout size={18} />
            </span>
            <div className="leading-tight">
              <div className="font-display font-bold text-base text-white">Koperasi Andalan Dapur Nusantara</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-amber-warm">Console Pengurus</div>
            </div>
          </Link>
          <button onClick={() => setOpen(false)} className="lg:hidden p-1 rounded hover:bg-white/10 text-white">
            <X size={18} />
          </button>
        </div>

        <nav className="px-3 py-4 space-y-1 flex-1 overflow-y-auto">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setOpen(false)}
              data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive
                    ? "bg-white/10 text-amber-warm border-l-2 border-amber-warm pl-[10px]"
                    : "text-cream/70 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <n.icon size={17} />
              <span className="font-medium">{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="px-4 py-4 space-y-3 border-t border-white/10">
          <div className="rounded-xl bg-white/5 p-3 flex items-center gap-3">
            <Avatar className="w-9 h-9">
              <AvatarFallback className="bg-amber-warm/20 text-amber-warm text-xs font-bold">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-white truncate">{user.name}</div>
              <div className="text-[10px] uppercase tracking-wide text-amber-warm">{ROLE_LABELS[user.role] || user.role}</div>
            </div>
          </div>
          <button onClick={doLogout} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-cream/65 hover:bg-white/5 hover:text-white transition" data-testid="admin-logout">
            <LogOut size={16} /> Keluar
          </button>
        </div>
      </aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 bg-white/85 backdrop-blur-md border-b border-stone-soft pt-safe">
          <div className="px-4 lg:px-8 h-14 lg:h-16 flex items-center gap-3">
            <button onClick={() => setOpen(true)} className="hidden md:block lg:hidden p-2 rounded-lg hover:bg-cream" data-testid="admin-sidebar-toggle">
              <Menu size={20} />
            </button>
            {/* Mobile: identitas console + role */}
            <div className="md:hidden flex items-center gap-2.5 flex-1 min-w-0">
              <span className="w-9 h-9 rounded-lg bg-ink text-amber-warm grid place-items-center shrink-0">
                <Sprout size={18} />
              </span>
              <div className="leading-tight min-w-0">
                <div className="text-sm font-semibold text-ink truncate">Console Pengurus</div>
                <div className="text-[10px] uppercase tracking-wide text-amber-700 truncate">{ROLE_LABELS[user.role] || user.role}</div>
              </div>
            </div>
            <div className="hidden md:block flex-1 max-w-xl relative">
              <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink/55" />
              <Input placeholder="Cari anggota, pesanan, produk, transaksi..." className="pl-10 bg-cream border-stone-soft rounded-lg" data-testid="admin-search-input" />
            </div>
            <Button className="hidden md:inline-flex bg-forest hover:bg-forest-dark text-white rounded-lg" data-testid="admin-quick-action">
              <Plus size={16} /> Aksi Cepat
            </Button>
            <button className="relative w-10 h-10 rounded-lg bg-cream border border-stone-soft grid place-items-center hover:bg-white transition" data-testid="admin-notif-button">
              <Bell size={17} />
              <span className="absolute top-2 right-2 w-2 h-2 bg-terracotta rounded-full"></span>
            </button>
          </div>
        </header>

        <main className="px-4 sm:px-5 lg:px-8 py-5 lg:py-6 pb-tabbar">
          <div className="pb-6 lg:pb-0"><Outlet /></div>
        </main>
      </div>

      <MobileTabBar items={tabs} tone="dark" testIdPrefix="admin-tab" />
    </div>
  );
}
